/**
 * Vue.js міксин для відстеження додавання товарів в кошик
 * Використовується в компонентах з кнопками додавання в кошик
 */
import ProductAddToCartTracker from '@/utils/productAddToCartTracker';
export default {
    data() {
        return {
            addToCartTracker: null
        };
    },
    mounted() {
        // Створюємо один глобальний екземпляр для всіх операцій
        if (!window.__globalAddToCartTrackerInstance) {
            window.__globalAddToCartTrackerInstance = new ProductAddToCartTracker();
        }
    },
    beforeDestroy() {
        if (this.addToCartTracker) {
            this.addToCartTracker.destroy();
        }
    },
    methods: {
        /**
         * Ініціалізує відстеження додавання в кошик
         */
        initAddToCartTracker() {
            this.addToCartTracker = new ProductAddToCartTracker();
        },
        /**
         * Відстежує додавання товару в кошик
         * @param {Object} product - Дані товару
         * @param {number} quantity - Кількість товару
         */
        trackAddToCart(product, quantity = 1) {
            if (!product || !product.id) {
                return;
            }
            // Сохраняем цену за лист для использования в begin_checkout
            this.saveM2PriceForCheckout(product);
            // Використовуємо глобальний трекер
            if (window.__globalAddToCartTrackerInstance) {
                window.__globalAddToCartTrackerInstance.trackAddToCartManual(product, quantity);
                return;
            }
            if (this.addToCartTracker) {
                this.addToCartTracker.trackAddToCartManual(product, quantity);
                return;
            }
            // Фолбек: відправляємо подію без глобального трекера
            if (!window || !window.dataLayer) return;
            // Витягуємо ціну з даних товару
            const price = this.extractPriceFromProduct(product);
            // Витягуємо бренд з title, якщо він не встановлений
            let finalBrand = (product.productBrand && product.productBrand.title) || '';
            if (!finalBrand && product.title) {
                finalBrand = this.extractBrandFromTitle(product.title);
            }
            // Визначаємо категорію з пріоритетом (як у productViewTracker):
            // 1. З роуту (каталогу) - найвищий пріоритет
            // 2. Збережена в sessionStorage (з попереднього кліку)
            // 3. З даних товару (name або title)
            // 4. Фолбек
            let finalCategory = '';
            // Спочатку спробуємо отримати з роуту (каталогу)
            const routeCategory = this.getCurrentCategoryFromRoute();
            if (routeCategory) {
                const categoryName = this.getCategoryNameFromCatalog(routeCategory);
                if (categoryName) {
                    finalCategory = categoryName;
                }
            }
            // Якщо не знайшли в роуті, спробуємо sessionStorage
            if (!finalCategory) {
                try {
                    if (typeof window !== 'undefined' && window.sessionStorage) {
                        finalCategory = sessionStorage.getItem('ga_last_item_category') || '';
                    }
                } catch (e) { /* no-op */ }
            }
            // Якщо все ще не знайшли, спробуємо з даних товару
            if (!finalCategory && product.category) {
                finalCategory = product.category.name || product.category.title || '';
            }
            if (!finalCategory) {
                finalCategory = 'Невизначена категорія';
            }
            const formattedProduct = {
                item_name: product.title || '',
                item_id: this.normalizeId(product.id), // Используем такую же логику как в трекере
                price: price,
                currency: 'UAH',
                item_brand: finalBrand,
                productBrand: { title: finalBrand }, // Добавляем как в трекере
                item_category: finalCategory, // Используем finalCategory как в трекере
                quantity: quantity || 1
            };
            window.dataLayer.push({ecommerce: null});
            window.dataLayer.push({
                event: 'add_to_cart',
                ecommerce: {
                    items: [formattedProduct]
                }
            });
        },
        /**
         * Сохраняет цену товара для использования в begin_checkout
         * @param {Object} product - Данные товара
         */
        saveM2PriceForCheckout(product) {
            // TODO: Implement price saving logic
        },
        /**
         * Витягує ціну з даних товару
         * @param {Object} product - Дані товару
         * @returns {number}
         */
        extractPriceFromProduct(product) {
            if (!product || !product.productPrice) {
                return 0;
            }
            
            // Перевіряємо структуру ціни
            if (product.productPrice.price && typeof product.productPrice.price.price === 'number') {
                return product.productPrice.price.price;
            }
            
            // Альтернативна структура
            if (typeof product.productPrice.price === 'number') {
                return product.productPrice.price;
            }
            
            // Якщо ціна в основному об'єкті
            if (typeof product.price === 'number') {
                return product.price;
            }
            
            return 0;
        },
        /**
         * Нормалізує ID до числа, якщо можливо, або повертає рядок/порожнє значення
         * @param {*} id - ID для нормалізації
         * @returns {number|string}
         */
    normalizeId(id) {
        if (id === null || id === undefined) return '';
        const num = Number(id);
        if (!Number.isNaN(num) && Number.isFinite(num)) return num;
        return ('' + id);
    },
    /**
     * Отримує поточну категорію з роуту
     * @returns {string}
     */
    getCurrentCategoryFromRoute() {
        try {
            // Перевіряємо, чи є Vue router доступний
            if (typeof window !== 'undefined' && window.$nuxt) {
                const route = window.$nuxt.$route;
                if (route && route.params && route.params.slug) {
                    return route.params.slug;
                }
            }
            // Альтернативний спосіб - через URL
            const path = window.location.pathname;
            const pathParts = path.split('/');
            const katalogIndex = pathParts.findIndex(part => part === 'katalog');
            if (katalogIndex !== -1 && pathParts[katalogIndex + 1]) {
                return pathParts[katalogIndex + 1];
            }
            return '';
        } catch (e) {
            return '';
        }
    },
    /**
     * Отримує назву категорії з каталогу за slug
     * @param {string} slug - Slug категорії
     * @returns {string}
     */
    getCategoryNameFromCatalog(slug) {
        if (!slug) {
            return '';
        }
        // Список каталогів з футера (правильні slug з бази даних)
        const catalogMap = {
            'monolitnii-polikarbonat': 'Монолітний полікарбонат',
            'profilyovanii-polikarbonat': 'Профільований полікарбонат',
            'sotovii-polikarbonat': 'Сотовий полікарбонат',
            'vstavka-do-pokrivli-iz-sendvich-paneli': 'Вставка до покрівлі із сендвіч панелі',
            'svitloprozori-fasadni-systemy': 'Світлопрозорі фасадні системи',
            'svitloprozori-pokrivelni-systemy': 'Світлопрозорі покрівельні системи',
            'aksessuary': 'Аксесуари',
            'rishennya-dlya-avtomobiliv': 'Рішення для автомобілів',
            'agroteplitsi': 'Агротеплиці'
        };
        const categoryName = catalogMap[slug];
        if (categoryName) {
            return categoryName;
        } else {
            return slug;
        }
    },
        /**
         * Витягує бренд з заголовка товару
         * @param {string} title - Заголовок товару
         * @returns {string}
         */
        extractBrandFromTitle(title) {
            if (!title) return '';
            const parseBrandFromTitle = (title) => {
                const t = (title || '').trim();
                if (!t) return '';
                // Пріоритет 1: Шукаємо відомі бренди з модифікаторами
                const knownBrands = ['BauGlas', 'RODECA', 'BAUGLAS'];
                for (const brand of knownBrands) {
                    const brandWithModifier = t.match(new RegExp(`\\b${brand}\\s+([A-Z][A-Za-z]*(?:\\s+[A-Z][A-Za-z]*)*)\\b`, 'i'));
                    if (brandWithModifier && brandWithModifier[0]) {
                        return brandWithModifier[0];
                    }
                    const brandMatch = t.match(new RegExp(`\\b${brand}\\b`, 'i'));
                    if (brandMatch && brandMatch[0]) {
                        return brandMatch[0];
                    }
                }
                // Додатковий пошук: шукаємо "BauGlas FSX Professional" як окремий випадок
                const bauGlasFSXProfessionalMatch = t.match(/\bBauGlas\s+FSX\s+Professional\b/i);
                if (bauGlasFSXProfessionalMatch && bauGlasFSXProfessionalMatch[0]) {
                    return bauGlasFSXProfessionalMatch[0];
                }
                // Додатковий пошук: шукаємо "BauGlas FSX" як окремий випадок
                const bauGlasFSXMatch = t.match(/\bBauGlas\s+FSX\b/i);
                if (bauGlasFSXMatch && bauGlasFSXMatch[0]) {
                    return bauGlasFSXMatch[0];
                }
                // Пріоритет 2: Шукаємо перед комою
                const commaIdx = t.indexOf(',');
                if (commaIdx > 0) {
                    const beforeComma = t.slice(0, commaIdx).trim();
                    if (beforeComma.length >= 2 && beforeComma.length <= 60) {
                        return beforeComma;
                    }
                }
                // Пріоритет 3: Шукаємо CamelCase (BauGlas, BauGlas FSX)
                const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+(?:\s+[A-Z]+)*\b/);
                if (camelMatch && camelMatch[0]) {
                    return camelMatch[0];
                }
                // Пріоритет 4: Шукаємо слова з великих літер (RODECA, BAUGLAS, FSX)
                const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
                if (capsMatch && capsMatch[0]) {
                    return capsMatch[0];
                }
                // Пріоритет 5: Беремо перше слово з 3+ літер
                const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
                if (firstWord && firstWord[0]) {
                    return firstWord[0];
                }
                return '';
            };
            return parseBrandFromTitle(title);
        },
        /**
         * Відстежує додавання комплекту товарів в кошик одним разом
         * @param {Array} products - Масив товарів з кількістю [{product, quantity}, ...]
         */
        trackAddToCartBundle(products) {
            console.log('🛒 [BUNDLE TRACK] trackAddToCartBundle called with:', {
                products: products,
                productsLength: products?.length,
                hasProducts: !!(products && Array.isArray(products) && products.length > 0)
            });
            
            if (!products || !Array.isArray(products) || products.length === 0) return;
            if (!window || !window.dataLayer) return;
            // TODO: Implement price saving for bundle products
            products.forEach(({product}) => {
                if (product) {
                    this.saveM2PriceForCheckout(product);
                }
            });
            // Форматуємо всі товари
            const formattedItems = products.map(({product, quantity}, index) => {
                console.log(`🛒 [BUNDLE TRACK] Processing product ${index + 1}:`, {
                    product: product,
                    quantity: quantity,
                    hasProduct: !!product,
                    hasId: !!(product && product.id),
                    productCategory: product?.category,
                    productCategoryName: product?.category?.name
                });
                1
                if (!product) return null;
                // Витягуємо ціну з даних товару
                const price = this.extractPriceFromProduct(product);
                // Витягуємо бренд з title, якщо він не встановлений
                let finalBrand = (product.productBrand && product.productBrand.title) || '';
                if (!finalBrand && product.title) {
                    finalBrand = this.extractBrandFromTitle(product.title);
                }
                // Визначаємо категорію з пріоритетом (як у productViewTracker):
                // 1. З роуту (каталогу) - найвищий пріоритет
                // 2. Збережена в sessionStorage (з попереднього кліку)
                // 3. З даних товару (name або title)
                // 4. Фолбек
                let finalCategory = '';
                // Спочатку спробуємо отримати з роуту (каталогу)
                const routeCategory = this.getCurrentCategoryFromRoute();
                if (routeCategory) {
                    const categoryName = this.getCategoryNameFromCatalog(routeCategory);
                    if (categoryName) {
                        finalCategory = categoryName;
                    }
                }
                // Якщо не знайшли в роуті, спробуємо sessionStorage
                if (!finalCategory) {
                    try {
                        if (typeof window !== 'undefined' && window.sessionStorage) {
                            finalCategory = sessionStorage.getItem('ga_last_item_category') || '';
                        }
                    } catch (e) { /* no-op */ }
                }
                // Якщо все ще не знайшли, спробуємо з даних товару
                if (!finalCategory && product.category) {
                    finalCategory = product.category.name || product.category.title || '';
                }
                if (!finalCategory) {
                    finalCategory = 'Невизначена категорія';
                }
                return {
                    item_name: product.title || '',
                    item_id: this.normalizeId(product.id), // Используем такую же логику как в трекере
                    price: price,
                    currency: 'UAH',
                    item_brand: finalBrand,
                    productBrand: { title: finalBrand }, // Добавляем как в трекере
                    item_category: finalCategory, // Используем finalCategory как в трекере
                    quantity: quantity || 1
                };
            }).filter(item => item !== null);
            if (formattedItems.length === 0) return;
            
            console.log('🛒 [BUNDLE TRACK] Final formatted items for bundle:', formattedItems);
            
            // Відправляємо одну подію з усіма товарами
            window.dataLayer.push({ecommerce: null});
            window.dataLayer.push({
                event: 'add_to_cart',
                ecommerce: {
                    items: formattedItems
                }
            });
            
            console.log('✅ [BUNDLE TRACK] Bundle add_to_cart event sent successfully!');
        },
        /**
         * Обробляє клік по кнопці додавання в кошик
         * @param {Object} product - Дані товару
         * @param {number} quantity - Кількість товару
         */
        handleAddToCart(product, quantity = 1) {
            // Відстежуємо додавання в кошик
            this.trackAddToCart(product, quantity);
            // Викликаємо оригінальний метод додавання в кошик
            if (this.addBasket) {
                this.addBasket();
            }
        }
    }
};